import { CalculatorResponse } from '@api';
import { BandChart, ErrorMessage, SkeletonLoader } from '@design';
import { useElementSize } from '@helpers';
import { getHorizonToIndexMap } from '@terminal/components/price-movement/components/price-movement-chart/utils/price-movement-chart-utils';
import { useCatalystInsightsList } from '@terminal/react-query-hooks/insights/useCatalystInsights';
import { useThemePerformance } from '@terminal/react-query-hooks/themes/useThemeOverview';
import { useLanguageStore } from '@terminal/stores/use-language-store/useLanguageStore';
import { PriceReturnPill } from '@terminal/views/theme-overview/theme-overview-drawer/components/theme-performance-section/price-return-pill/PriceReturnPill';
import {
  SimpleHorizon,
  SimpleHorizonSelector,
} from '@terminal/views/theme-overview/theme-overview-drawer/components/theme-performance-section/simple-horizon-selector/SimpleHorizonSelector';
import { useChartTheme } from '@terminal/views/turbo-chart/hooks/use-chart-theme/useChartTheme';
import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BandChartInsightsOverlay } from './BandChartInsightsOverlay';
import styles from './ThemePerfBandChart.module.scss';
import {
  buildCatalystInsightsListPayload,
  shouldShowBandChartInsights,
} from './utils';

interface ThemePerfBandChartProps {
  entityTags: string[];
  isFetching?: boolean;
  defaultHorizon?: SimpleHorizon;
  showHorizonSelector?: boolean;
  themeId: string;
}

export const ThemePerfBandChart = ({
  themeId,
  entityTags,
  isFetching,
  defaultHorizon = '1M',
  showHorizonSelector = true,
}: ThemePerfBandChartProps) => {
  const { t } = useTranslation('widget');
  const { colors } = useChartTheme(['soft']);
  const languageSetting = useLanguageStore(state => state.languageSetting);
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const { width: chartWidth } = useElementSize(chartContainerRef);
  const [selectedHorizon, setSelectedHorizon] =
    useState<SimpleHorizon>(defaultHorizon);
  const horizonToIndexMap = getHorizonToIndexMap(1);

  const {
    data: themePerformanceData,
    isFetching: isThemePerformanceFetching,
    isError: isThemePerformanceError,
  } = useThemePerformance(
    entityTags,
    horizonToIndexMap.get(selectedHorizon.toLowerCase())
  );

  const total = entityTags.length > 10 ? 10 : entityTags.length;

  const formatData = (data?: CalculatorResponse) =>
    data?.aggregate.map((d, idx) => ({
      index: idx,
      value: d.value,
      date: new Date(d.index),
    })) ?? [];

  const handleHorizonChange = (horizon: SimpleHorizon) => {
    setSelectedHorizon(horizon);
  };

  const tsData = useMemo(
    () => formatData(themePerformanceData),
    [themePerformanceData]
  );
  const xAxisCalendarStep = selectedHorizon === '1M' ? 'week' : 'month';

  const { minDate, maxDate } = useMemo(() => {
    return {
      minDate: tsData[0]?.date,
      maxDate: tsData.at(-1)?.date,
    };
  }, [tsData]);

  const catalystInsightsListEnabled = Boolean(themeId && minDate && maxDate);

  const catalystInsightsListPayload = useMemo(
    () => buildCatalystInsightsListPayload(themeId, minDate, maxDate),
    [themeId, minDate, maxDate]
  );

  const { data: catalystInsights, isLoading: isCatalystInsightsLoading } =
    useCatalystInsightsList(
      catalystInsightsListPayload,
      catalystInsightsListEnabled
    );

  const shouldShowInsights = shouldShowBandChartInsights({
    isCatalystInsightsLoading,
    catalystInsights,
    minDate,
    maxDate,
    tsDataLength: tsData.length,
  });

  if (isFetching || isThemePerformanceFetching) {
    return (
      <div className={styles.root}>
        <SkeletonLoader
          areas={[
            {
              styles: {
                width: '100%',
                height: '248px',
                borderRadius: '8px',
              },
            },
          ]}
        />
      </div>
    );
  }

  if (isThemePerformanceError || !themePerformanceData) {
    return (
      <div className={styles.chartErrorWrapper}>
        <ErrorMessage
          displayIcon="error"
          title={t('widget:themeOverview.errorTitle')}
        />
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div className={styles.chartTitleWrapper}>
        {showHorizonSelector && (
          <SimpleHorizonSelector
            selectedHorizon={selectedHorizon}
            horizons={['1W', '1M', '3M', '6M', '12M']}
            handleHorizonChange={handleHorizonChange}
          />
        )}
      </div>
      <div className={styles.headerWrapper}>
        <div className={styles.textWrapper}>
          <p className={styles.chartTitleText}>
            {t('widget:themeOverview.themePerformance')}
          </p>
          <p className={styles.descriptionText}>
            {`${t('themeOverview.equalWeightedBasket')} ${t(
              'themeOverview.topCompanies',
              { total }
            )}`}
          </p>
        </div>
        <PriceReturnPill horizon={selectedHorizon} themeId={themeId} />
      </div>
      <div className={styles.chartAndHorizonsWrapper}>
        <div className={styles.chartContainer} ref={chartContainerRef}>
          <div
            className={styles.bandChartWrapper}
            style={
              {
                '--band-chart-color': colors.default.value,
              } as React.CSSProperties
            }
          >
            <BandChart
              tsData={tsData}
              showLine
              domainLimited={false}
              includeXScalePadding={false}
              isBordered
              fillColors={{
                positive: colors.threshold.positive.value,
                negative: colors.threshold.negative.value,
              }}
              isPercent
              locale={languageSetting}
              xAxisCalendarStep={xAxisCalendarStep}
            />
          </div>
          {shouldShowInsights && chartWidth > 0 && (
            <BandChartInsightsOverlay
              insights={catalystInsights ?? []}
              chartWidth={chartWidth}
              tsData={tsData}
            />
          )}
        </div>
      </div>
    </div>
  );
};
